
import React, { useState, useMemo } from 'react';
import { useGetProductsWithDetails, ProductWithDetails } from '@/hooks/use-products-with-details';
import { ProductForm } from './ProductForm';
import { ProductList } from './ProductList';
import { EditProductDialog } from './EditProductDialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export type SortField = 'name' | 'category' | 'unit_of_measure';
export type SortDirection = 'asc' | 'desc';

export function ProductTab({ hideDelete }: { hideDelete?: boolean }) {
  const { data: products, isLoading, refetch } = useGetProductsWithDetails();
  const [editingProduct, setEditingProduct] = useState<ProductWithDetails | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  const handleEditProduct = (product: ProductWithDetails) => {
    setEditingProduct(product);
    setEditDialogOpen(true);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const categories = useMemo(() => {
    if (!products) return [];
    const unique = new Set(products.map(p => p.categories?.name).filter(Boolean) as string[]);
    return Array.from(unique).sort();
  }, [products]);

  const filteredProducts = useMemo(() => {
    if (!products) return [];
    let result = products;

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(term) ||
        p.categories?.name?.toLowerCase().includes(term) ||
        p.unit_of_measure?.toLowerCase().includes(term)
      );
    }

    // Category filter
    if (categoryFilter && categoryFilter !== 'all') {
      result = result.filter(p => p.categories?.name === categoryFilter);
    }

    // Sort
    result = [...result].sort((a, b) => {
      let aVal = '';
      let bVal = '';
      if (sortField === 'name') {
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
      } else if (sortField === 'category') {
        aVal = (a.categories?.name || '').toLowerCase();
        bVal = (b.categories?.name || '').toLowerCase();
      } else if (sortField === 'unit_of_measure') {
        aVal = (a.unit_of_measure || 'kg').toLowerCase();
        bVal = (b.unit_of_measure || 'kg').toLowerCase();
      }
      const cmp = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [products, searchTerm, categoryFilter, sortField, sortDirection]);

  const hasActiveFilters = searchTerm.trim() || (categoryFilter && categoryFilter !== 'all');

  return (
    <div className="space-y-6">
      <ProductForm onProductAdded={refetch} />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setSearchTerm(''); setCategoryFilter('all'); }}
            className="gap-1 text-muted-foreground"
          >
            <X className="h-3 w-3" />
            Clear filters
          </Button>
        )}

        <span className="text-sm text-muted-foreground ml-auto">
          {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
        </span>
      </div>
      
      <ProductList 
        products={filteredProducts}
        isLoading={isLoading}
        onEdit={handleEditProduct}
        onDeleted={refetch}
        hideDelete={hideDelete}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
      />

      <EditProductDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        product={editingProduct}
        onProductUpdated={refetch}
      />
    </div>
  );
}
